import React, { useEffect, useRef, useCallback, useState } from "react";
import type { CommunityPost } from "./interfaces/post.interface";
import { usePostStore } from "../../../../store/postStore";
import FacebookPost from "./FacebookPost";

type Props = {
  onEdit?: (post: CommunityPost) => void;
  onDelete?: (post: CommunityPost) => void;
};

const PostFeed: React.FC<Props> = ({ onEdit, onDelete }) => {
  const {
    posts,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadFeed,
    loadMore,
  } = usePostStore();

  const loaderRef = useRef<HTMLDivElement>(null);

  // Initial load
  useEffect(() => {
    if (posts.length === 0) {
      loadFeed();

    }
  }, [posts.length, loadFeed]);

  // Infinite scroll
  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  // Initial loading
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
        <button
          onClick={() => loadFeed(true)}
          className="mt-3 px-4 py-2 rounded bg-primary-600 text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty
  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No posts yet. Be the first to share something!
      </div>
    );
  }
  

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <FacebookPost
          key={post.id}
          post={post}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}

      {/* Load more trigger */}
      <div ref={loaderRef} className="h-4" />

      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      )}

      {!hasMore && posts.length > 0 && (
        <p className="text-center text-sm text-gray-400 py-4">
          No more posts
        </p>
      )}
    </div>
  );
};

export default PostFeed;